import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas, { Options } from 'html2canvas';
import { LanguageService } from '@igo2/core/language';
import { IgoMap, Feature, Layer, FeatureMotion, moveToOlFeatures, isLayerItem, zoneMtm } from '@igo2/geo';
import { transform } from 'ol/proj';
import { ScaleLine } from 'ol/control';
import Map from 'ol/Map';
import View, { FitOptions } from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OlVectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Fill, Stroke, Style, Circle as CircleStyle } from 'ol/style';
import { defaults as defaultControls } from 'ol/control';
import BaseLayer from 'ol/layer/Base';
import { VectorLayer } from '@igo2/geo';
import ImageLayer from 'ol/layer/Image';
import { Extent } from 'ol/extent';
import { ImageWMS, Vector } from 'ol/source';
import { Coordinate } from 'ol/coordinate';
import { Point as OlPoint} from 'ol/geom';
import OlFeature from 'ol/Feature';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface MetadataOptions {
    currentYear?: string;
    mapYear?: string;
    mapProjection?: string;
    metadataRealisation?: string; // Set in base context.
}
export interface ExportFicheDroitOptions {
  showGuides?: boolean; //to show page elements rectangle borders
  zoomAuto?:boolean; // to ensure the map is zommed to the selected row
  overviewMapExtent?:Extent; // the extent ot the overviewMap
  overviewBaseMapName?:string; // the name, from the main map base map layers, of the base map to use. If null the active basemap is used
  overviewRegAdmName?:string; // the name, from the map map layers, of the admin regions.
  overviewMapMaxZoomLevel?:number; // the max zoom level for the overview map - prevents zooming in too much when the extent is not provided
  reportTitles?:string[]; // Titles to use in the header instead of the second line.
  metadata?:MetadataOptions;
  legendImage?: string;
  mapCenterPoint?: Coordinate; //If provided, used as the map center point instead of the input feature location
  featureSelectionLayer?: VectorLayer; //The layer where the selected feature is shown. From the entity store pf the workspace.
}

@Injectable({
  providedIn: 'root'
})
export class ImpressionGdfService {

  private languageService = inject(LanguageService);
  isLayerItem = isLayerItem;

  constructor() {}
  public getReportTitles(row: Feature): [string, string]  {
    const labelDroit = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.titreBail');
    const labelDossier = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.dossier');
    const dataFins = row?.properties['Fins'] ?? '';
    const fins = this.formatFins(dataFins);
    const noDossier = row?.properties['No dossier'] ?? '';

    return [
      fins,
      `${labelDossier} ${noDossier}`
    ];
  }

  async exportFicheDroit(row: Feature, igoMap: IgoMap, options: ExportFicheDroitOptions = {}) {
    const {
      showGuides = false,
      zoomAuto = true,
      overviewMapExtent,
      overviewBaseMapName,
      overviewRegAdmName,
      overviewMapMaxZoomLevel = 10,
      reportTitles = this.getReportTitles(row),
      metadata = {}
    } = options;

    if (overviewBaseMapName === null || overviewRegAdmName === null) {
      console.error("Invalid overview layer name");
       return;
    }

    const currentYear = metadata?.currentYear ?? new Date().getFullYear().toString();
    const mapYear = metadata?.mapYear ?? '';
    const mapProjection = metadata?.mapProjection ?? 'EPSG:3857';
    metadata.currentYear = currentYear;
    metadata.mapYear = mapYear;
    metadata.mapProjection = mapProjection;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'legal' });
    const pageElements = this.getLayoutElementsRects(doc);

    if(showGuides) {
      this.drawGuides(doc, pageElements);
    }

    const { mapImage, scalebarImage } = await this.getMapCurrentScreenshot(row, zoomAuto, igoMap.ol, pageElements.mapFrame, options.mapCenterPoint, options.featureSelectionLayer);
    await this.addMap(doc, mapImage, pageElements.mapFrame);
    await this.addMapFrame(doc, pageElements.mapFrame);
    await this.addHeader(doc, row, pageElements.title, reportTitles);
    await this.addOverviewMap(doc, igoMap, row, options.mapCenterPoint, pageElements.ovMap, overviewMapExtent,
                              overviewBaseMapName, overviewRegAdmName, overviewMapMaxZoomLevel);
    await this.addMapFrame(doc, pageElements.ovMap);
    await this.addLegend(doc, pageElements.legend, options.legendImage);
    await this.addMetadata(doc, scalebarImage, metadata, pageElements.metadata);
    await this.addLogo(doc, pageElements.logo);
    await this.addCenterCoordinates(doc, row, pageElements.coordinates, igoMap.projectionCode, options.mapCenterPoint);

    const filename = this.getFileName(row);
    doc.save(filename);
  }

  //Mainly for debugging purpose - Draw an simple rectangle for each pageElement of the layout
  private drawGuides(doc: jsPDF, layout: Record<string, Rect>) {
    Object.values(layout).forEach(rect => {
      doc.rect(rect.x, rect.y, rect.w, rect.h);
    });
  }


  // Definitions of the layout per ArcGIS Pro Layout
  private getLayoutElementsRects(pdf: jsPDF): Record<string, Rect> {

  //specify everything in inches to match docmentation
  const hardwareMargins = [0.25, 0.25, 0.25, 0.25]; // top, right, bottom, left
  const mapFrameSize = [7.8358, 9.8711];
  const mapFramePadding = [0.1311, 0, 0.3251, 0];
  const ovMapSize = [1.24, 1.944];
  const ovMapAnchor = [0.335, 13.2896];
  const legendOffset = 0.1575;
  const logoFrameSize = [1.5241, 0.6866];
  const logoAnchor = [6.65,13.3474];
  const metadataFrameSize = [2, 1.944]; // Not really used other than to offset X from the legend. Y = ovMapSize[1] so the tops aling
  const metadataFrameAnchor = [4.65, 13.2896]; // logoAnchor[0]-metadataFrameSize[0], ovMapAnchor[1]
  const bottomElementsOffset = 0.7061;
  const coordinatesFrameSize = [7.8358, 0.2];

  const scaleFactor = 72 / pdf.internal.scaleFactor; // doc units
  const pageSize = pdf.internal.pageSize;
  const pageWidth = pageSize.getWidth() / scaleFactor;
  const pageHeight = pageSize.getHeight() / scaleFactor;

  //Documentation places the objects from the bottom of the page.
  //Infer title position
  const titleX = (pageWidth - hardwareMargins[3] - hardwareMargins[2] - mapFrameSize[0]) / 2 + hardwareMargins[3];
  const titleY = hardwareMargins[0];
  const titleBottom = pageHeight - hardwareMargins[2] - bottomElementsOffset - ovMapSize[1] - mapFramePadding[2] - mapFrameSize[1];
  const titleH = titleBottom - titleY;

  const mapFrameY = titleBottom + mapFramePadding[0];
  const ovMapY = ovMapAnchor[1] - ovMapSize[1];

  //whatever's left under the map frame between the ovMap and the metadataFrame padded by a legendffset on each side
  const legendW = mapFrameSize[0] - ovMapSize[0] - logoFrameSize[0] - metadataFrameSize[0]
                  - legendOffset - legendOffset;
  const legendX = titleX + ovMapSize[0] + legendOffset;

  const metadataX = metadataFrameAnchor[0];
  const metadataY = metadataFrameAnchor[1]-metadataFrameSize[1];

  const logoX = logoAnchor[0];
  const logoY = logoAnchor[1] - logoFrameSize[1];

  const coordinatesY = mapFrameY + mapFrameSize[1] + 0.1;

  const toPdfUnits = (val: number) => val * scaleFactor;

  //return in page units
  const title:Rect = { x: toPdfUnits(titleX), y: toPdfUnits(titleY), w: toPdfUnits(mapFrameSize[0]), h: toPdfUnits(titleH) };
  const mapFrame:Rect = { x: toPdfUnits(titleX), y: toPdfUnits(mapFrameY), w: toPdfUnits(mapFrameSize[0]), h: toPdfUnits(mapFrameSize[1]) };
  const ovMap:Rect = { x: toPdfUnits(titleX), y: toPdfUnits(ovMapY), w: toPdfUnits(ovMapSize[0]), h: toPdfUnits(ovMapSize[1]) };
  const legend:Rect = { x: toPdfUnits(legendX), y: toPdfUnits(ovMapY), w: toPdfUnits(legendW), h: toPdfUnits(ovMapSize[1]) };
  // eslint-disable-next-line max-len
  const metadata:Rect = { x: toPdfUnits(metadataX), y: toPdfUnits(metadataY), w: toPdfUnits(metadataFrameSize[0]), h: toPdfUnits(metadataFrameSize[1]) };
  const logo:Rect = { x: toPdfUnits(logoX), y: toPdfUnits(logoY), w: toPdfUnits(logoFrameSize[0]), h: toPdfUnits(logoFrameSize[1]) };
  const coordinates: Rect = { x: toPdfUnits(titleX), y: toPdfUnits(coordinatesY),
                              w: toPdfUnits(coordinatesFrameSize[0]), h: toPdfUnits(coordinatesFrameSize[1]) };

  return {title, mapFrame, ovMap, legend, metadata, logo, coordinates};
  }

  private async getMapCurrentScreenshot(row: any, zoomAuto:boolean, olMap: Map, rect: Rect, mapCenterPoint: Coordinate, layerSelection:VectorLayer):
                                        Promise<{ mapImage: string, scalebarImage: string }> {
    const featureBasedCenter = transform(row.geometry.coordinates, row.projection, 'EPSG:3857');
    const newCenter = mapCenterPoint ? mapCenterPoint : featureBasedCenter;

    const curSize = olMap.getSize();
    const curResolution = olMap.getView().getState().resolution;
    const curCenter = olMap.getView().getState().center;


    const mapScale = curResolution * 96.0 / 0.0254; // browser map is at 96dpi

    //const pdfPrintResolution = 96; //dpi - screen resolution: for faster testing
    const pdfDpi = 300;
    const printResolution = mapScale / (pdfDpi / 0.0254); // target OL resolution for 300dpi print - OL resolution is m/px

    const mapFrameSizeInches = [rect.w / 25.4, rect.h / 25.4]; //in inches
    const imageSize = mapFrameSizeInches.map(x => x * pdfDpi); // width/height in px

    const curOverlayContainer = olMap.getOverlayContainerStopEvent();
    if (curOverlayContainer) {
      curOverlayContainer.style.display = 'none';
    }
    
    //hide the marker on the selection layer 
    const hideSelectionMarker:boolean = layerSelection != null;

    if (hideSelectionMarker) {
      this.toggleOverLayLayer(olMap, layerSelection, false);
    }
    // Resize and reposition the map
    olMap.getTargetElement().style.width = `${imageSize[0]}px`;
    olMap.getTargetElement().style.height = `${imageSize[1]}px`;
    olMap.updateSize();
    olMap.getView().setResolution(printResolution);
    olMap.getView().setCenter(newCenter);

    if (zoomAuto) {
      olMap.getView().setZoom(17);
    }

    //Force render
    olMap.renderSync();

    // Wait for rendering to complete
    const renderStatus = await this.waitForMapRender(olMap);

    if (renderStatus !== 'rendercomplete') {
      console.warn('Map did not finish rendering in time. Proceeding anyway.');
    }

    // Capture scale bar image
    const scalebarCanvas = await this.getMapScaleCurrentScreenshot(olMap);

    console.debug('Main map html2canvas start');
    const canvas = await html2canvas(olMap.getViewport(), {
      width: imageSize[0],
      height: imageSize[1]
    });
    console.debug('Main map canvas done');

    // Reset map to original state
    olMap.getTargetElement().style.width = '';
    olMap.getTargetElement().style.height = '';
    olMap.updateSize();
    olMap.getView().setResolution(curResolution);
    olMap.getView().setCenter(curCenter);

    if (curOverlayContainer) {
      curOverlayContainer.style.display = '';
    }

    if (hideSelectionMarker) {
      this.toggleOverLayLayer(olMap, layerSelection, true);
    }

    const mapImage = canvas.toDataURL('image/jpeg', 0.8);
    this.disposeCanvas(canvas);
    const scalebarImage = scalebarCanvas.toDataURL();
    this.disposeCanvas(scalebarCanvas);

    return { mapImage, scalebarImage };
  }

  private async getMapScaleCurrentScreenshot(olMap: Map): Promise<HTMLCanvasElement> {
    console.debug('getMapScaleCurrentScreenshot Start');

    const barScaleContainer = document.createElement('div');
    barScaleContainer.setAttribute('id', 'bar-scale-container');

    //Also see the waitForScaleLineRendered for style modifications after initial rendering
    barScaleContainer.setAttribute('style', `
      height: 50px;
      z-index: -1;
      top: 0px;
      left: 0px;
      position: absolute;`
    );
    document.body.appendChild(barScaleContainer);

    const control = new ScaleLine({
      target: barScaleContainer,
      bar: true,
      dpi: 300,
      text: false,
      steps: 4,
      minWidth: 117,
      maxWidth: 156 //metadataFramesize[0]*90
    });
    olMap.addControl(control);



    // Wait until scale bar is visually rendered
    await this.waitForScaleLineRendered(barScaleContainer);
    // Increase font size
    const scaleTextElements = barScaleContainer.querySelectorAll('.ol-scale-step-text');
    scaleTextElements.forEach(el => {
      (el as HTMLElement).style.fontSize = '18px'; // Adjust size as needed
    });
    barScaleContainer.style.width = this.getBarScalePixelWidth(barScaleContainer);
    
    console.debug('getMapScaleCurrentScreenshot html2canvas start');
    const canvas = await html2canvas(barScaleContainer, {
      scale: 3.125,
      //backgroundColor: null,
      //allowTaint: true,
      useCORS: true
    });
    console.debug('getMapScaleCurrentScreenshot html2canvas end');

    barScaleContainer.remove();
    olMap.removeControl(control);

    return canvas;
  }


private waitForScaleLineRendered(container: HTMLElement): Promise<void> {
  return new Promise(resolve => {
    const check = (last: boolean) => {
      const olScaleBar = container.querySelector('.ol-scale-bar') as HTMLElement;
      const widthReady = olScaleBar?.offsetWidth > 10;
      const hasText = container.innerText.trim().length > 0;

      //This is where we set the scale bar font size
      if (widthReady && hasText) {
        const elements = container.querySelectorAll('.ol-scale-step-text');
        elements.forEach((el: HTMLElement) => {
          el.style.fontSize = '8px';
        });

        if (last) {
          resolve();
        } else {
          requestAnimationFrame(() => check(true));
        }
      } else {
        requestAnimationFrame(() => check(false));
      }
    };

    check(false);
  });
}

  private getBarScalePixelWidth(barScaleContainer : HTMLDivElement) : string {
      const childOlScaleBar = barScaleContainer.querySelector('.ol-scale-bar') as HTMLElement;
      //Set the bottom value to avoid truncating the image.
      childOlScaleBar.style.bottom = '10px';
      const scaleBarStepTexts = childOlScaleBar.querySelectorAll('.ol-scale-step-text');
      // The last step text goes out of the scale delimeter to the right. I'll take its width and divide it by 2
      const childOlScaleBarLastStepText = scaleBarStepTexts[scaleBarStepTexts.length - 1] as HTMLElement;
      return (childOlScaleBar.offsetWidth + childOlScaleBarLastStepText.offsetWidth / 2) + 'px';
    }

  private async addHeader(pdf: jsPDF, row: Feature, rect: Rect, reportTitles:string[]): Promise<void> {
    // Create container
    const scaleFactor = pdf.internal.scaleFactor;
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-10000px';// Hide off-screen
    container.style.width = `${rect.w * scaleFactor}px`;

    container.innerHTML = `
      <div style="text-align: center; font-size: 9px;">
        <div style="font-size: 10px;font-weight: bold;">${reportTitles[0]}</div>
        <div>${reportTitles[1]}</div>
      </div>
    `;

    document.body.appendChild(container);
    const canvas = await html2canvas(container, { scale: 3.125 });
    if (canvas) {
      const imgData = canvas.toDataURL('image/png');
      this.disposeCanvas(canvas);
      // Calculate image dimensions to fit in the given rect
      const imgProps = pdf.getImageProperties(imgData);
      const aspectRatio = imgProps.width / imgProps.height;
      const imgHeight = rect.w / aspectRatio;

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', rect.x, rect.y, rect.w, imgHeight);
    }
     // Clean up
    document.body.removeChild(container);
    container.remove();
  }

  private async addMap(pdf: jsPDF, mapImage:string, rect:Rect): Promise<void> {
    pdf.addImage(mapImage, rect.x, rect.y, rect.w, rect.h);

  }

  private async addLegend(doc: jsPDF, rect: Rect, legendImage: string): Promise<void> {
    await this.addImageToPdf(doc,legendImage, rect,
                             {preserveOriginalSize: false, align:{horizontal: "left", vertical:"top"}});
  }

  private async addLogo(doc: jsPDF, rect: Rect): Promise<void> {
    await this.addImageToPdf(doc,'logoMrnfBlack.png', rect, {preserveOriginalSize: false, align:{horizontal: "right", vertical:"bottom"}});
  }
  private async addCenterCoordinates(doc: jsPDF, row: any, rect: Rect, projection: string, mapCenterPoint?:Coordinate) : Promise<void> {
    const curFontSize = doc.getFontSize();
    const curTextColor = doc.getTextColor();
    const curFont = doc.getFont();

    doc.setFont(curFont.fontName, 'bold');
    doc.setFontSize(7);
    doc.setTextColor(0,0,0);
    const label = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.prefixCoordinates');



    if (mapCenterPoint) {
      const text = this.getMTMCoordinate(label, mapCenterPoint, projection);
      if (text) {
        doc.text(text, rect.x, rect.y);
      } 
    }
    else {
      const x = row?.properties['Coord Est'];
      const y = row?.properties['Coord Nord'];
      const fuseau = row?.properties['Fuseau'];
      const projCode = row?.properties?.Projection;
      const proj = (projCode === 'U') ? 'UTM' : 'MTM';
      const text = `${label} ${proj} ${fuseau} ${x} ${y}`;
      doc.text(text, rect.x, rect.y);
    }
    doc.setFontSize(curFontSize);
    doc.setFont(curFont.fontName, curFont.fontStyle);
  }
  private async addOverviewMap(
    pdf: jsPDF,
    mainMap: IgoMap,
    row: any,
    mapCenterPoint: Coordinate,
    rect: Rect,
    overviewMapExtent:Extent,
    overviewBaseMapName:string,
    overviewRegAdmName:string,
    overviewMapMaxZoomLevel:number
  ): Promise<void> {
    console.debug('addOverviewMap Start');

    const dpi = 300;
    const pxToMm = (px: number) => px * (25.4 / dpi);
    const mmToPx = (mm: number) => mm * (dpi / 25.4);

    // 1. Measure header
    const headerDiv = document.createElement('div');
    headerDiv.style.position = 'fixed';
    headerDiv.style.left = '-9999px';
    headerDiv.style.width = `${mmToPx(rect.w)}px`;
    headerDiv.innerHTML = `
      <div style="
        background: rgb(9,87,151);
        color: white;
        font-size: 18px;
        font-weight: bold;
        padding: 0.25rem;
        font-family: Arial;
        border-bottom: 1px solid black;
        text-align: center;
        line-height:24px;
      ">Localisation</div>
    `;
    document.body.appendChild(headerDiv);

    await this.waitForElementToLayout(headerDiv);
    const headerCanvas = await html2canvas(headerDiv, { scale: 4 });
    const headerImage = headerCanvas.toDataURL('image/png');
    const headerHeightMm = pxToMm(headerCanvas.height / 4);
    const headerWidthMm = pxToMm(headerCanvas.width / 4);
    this.disposeCanvas(headerCanvas);
    document.body.removeChild(headerDiv);
    headerDiv.remove();

    // 2. Calculate map container size
    const mapHeightMm = rect.h - headerHeightMm;
    const renderWidthPx = mmToPx(rect.w);
    const renderHeightPx = mmToPx(mapHeightMm);

    // 3. Create offscreen map container
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.innerHTML = `
      <style>
        #ovMap {
          width: ${renderWidthPx}px;
          height: ${renderHeightPx}px;
        }
      </style>
      <div id="ovMap"></div>
    `;
    document.body.appendChild(container);

    const mapElement = container.querySelector('#ovMap') as HTMLElement;

    // 4. Setup OpenLayers overview map
    const map = new Map({
      target: mapElement,
      view: new View({
        center: [0, 0],
        zoom: 2
      }),
      controls: defaultControls({ attribution: false })
    });

    // Add base and operational layers
    let baseMap:Layer;
    if (overviewBaseMapName) {
       baseMap = mainMap.layerController.baseLayers.find(l => l.options?.name === overviewBaseMapName);
    } else {
       baseMap = mainMap.layerController.baseLayers.find(l => l.visible);
    }
    //
    const regAdmLayer = mainMap.layerController.all.find(l => this.isLayerItem(l) && l.options?.name === overviewRegAdmName) as Layer;

    if (baseMap) this.addLayerToMap(map, baseMap, true);
    if (regAdmLayer) this.addLayerToMap(map, regAdmLayer, true);


    const featureBasedCenter = transform(row.geometry.coordinates, row.projection, 'EPSG:3857');
    const newCenter = mapCenterPoint ? mapCenterPoint : featureBasedCenter;

    // Add feature as red dot
  const pointFeature = new OlFeature({geometry: new OlPoint(newCenter) }); 
  const vectorLayer = new OlVectorLayer({
    source: new VectorSource({
      features: [pointFeature]
    }),
    style: new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({ color: 'red' }),
        stroke: new Stroke({ color: 'white', width: 2 })
      }),
      stroke: new Stroke({ color: 'blue', width: 2 }),
      fill: new Fill({ color: 'rgba(0, 0, 255, 0.1)' })
    })
  });
    const basemapZindex = baseMap.ol.getZIndex();
    vectorLayer.setZIndex(basemapZindex+1);

    // Fit view to feature
    if (overviewMapExtent) {
      console.debug("OverviewMap:Mapextents provided");
       map.getView().fit(overviewMapExtent, { padding: [10, 10, 10, 10]});
    } else {
      console.debug("OverviewMap:Using default extents");
      const extent = vectorLayer.getSource().getExtent();
      map.getView().fit(extent, { padding: [10, 10, 10, 10], maxZoom: overviewMapMaxZoomLevel });
    }


    map.addLayer(vectorLayer);
    //Force render
    map.renderSync();

    // Wait for rendering to complete
    const renderStatus = await this.waitForMapRender(map);

     if (renderStatus !== 'rendercomplete') {
       console.warn('Map did not finish rendering in time. Proceeding anyway.');
     }

     await this.timeout(100); // let fonts/layouts settle

    const mapCanvas = await html2canvas(map.getViewport().querySelector('canvas'), {
      width: renderWidthPx,
      height: renderHeightPx
    });

    if (!mapCanvas) {
      console.error('Map canvas not found');
      document.body.removeChild(container);
      return;
    }

    const mapImage = mapCanvas.toDataURL('image/png');
    this.disposeCanvas(mapCanvas);
    // 6. Cleanup
    document.body.removeChild(container);
    container.remove();

    map.getLayers().forEach((lyr: any) => {
      const src = lyr.getSource?.();
      // clear vector features
      if (src?.clear) {
        try { src.clear(true); } catch {}
      }
      // avoid leaving style functions capturing references
      try { lyr.setStyle(null); } catch {}
    });

    map.setTarget(null);
    map.getLayers().clear();
    map.dispose?.();

    // 7. Draw header and map image in PDF
    pdf.addImage(headerImage, 'PNG', rect.x, rect.y, headerWidthMm, headerHeightMm);
    pdf.addImage(mapImage, 'PNG', rect.x, rect.y + headerHeightMm, rect.w, mapHeightMm);

    console.debug('addOverviewMap End');
  }

  private async addMetadata(
    doc: jsPDF,
    scalebarImage: string,
    metadata:MetadataOptions,
    rect: Rect): Promise<void> {
    const dpi = 300;
    const pxToMm = (px: number) => px * (25.4 / dpi);
    const mmToPx = (mm: number) => mm * (dpi / 25.4);

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.width = `${mmToPx(rect.w)}px`;
    container.innerHTML = `
      <style>
          .details {
              font-size: 18pt;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              gap: 1rem;
          }
          .scale-bar {
              height: 125px;
              width: 100%;
          }

          table {
              width: 100%;

              th {
                  text-align: left;
              }
          }
          .title {
              font-size:21pt;
              font-weight: bold;
              margin-bottom: 10px;
          }
      </style>
      <div class="details">
      <div>
        <div class="title">${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.metadonnees')}</div>
        <div>${metadata.mapProjection}</div>
        <div class="scale-bar"></div>
      </div>
      <div style="line-height:normal;">
          <div class="title">${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.sources')}</div>
          <table>
            <tr>
                <th>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.donnees')}</th>
                <th>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.organisme')}</th>
                <th>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.annee')}</th>
            </tr>
            <tr>
                <td>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.asisseCarto')}</td>
                <td>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.mrnf')}</td>
                <td>${ metadata.currentYear }</td>
            </tr>
            ${metadata.mapYear?.trim() ? `
            <tr>
                <td>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.photoAerienne')}</td>
                <td>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.mrnf')}</td>
                <td>${ metadata.mapYear }</td>
            </tr>
            ` : ''}
          </table>

      </div>
      <div style="line-height:normal;">
        <div class="title" style="font-size:19pt;">${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.realisation')}</div>
        <div>${metadata.metadataRealisation}</div>
        <div>${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.copyRight')}</div>
      </div>
      </div>
    `;


    document.body.appendChild(container);

    // 2. Wait for layout and measure height
    await this.waitForMetadataElementToLayout(container);

    const metadataCanvas = await html2canvas(container, { backgroundColor: null, scale: 4 }); // background transparent, scale 300dpi/72dpi
    const metadataImage = metadataCanvas.toDataURL('image/png');
    const metadataHeightPx = metadataCanvas.height;
    const metadataHeightMm = pxToMm(1+metadataHeightPx/4); //to offset the scaling in html2canvas
    const metadataWidthMm = pxToMm(1+metadataCanvas.width/4);
    this.disposeCanvas(metadataCanvas);
    document.body.removeChild(container);
    doc.addImage(metadataImage, 'PNG', rect.x, rect.y, metadataWidthMm, metadataHeightMm);
    const { width: scabeBarWidth, height: scaleBarHeight } = await this.getImageSizeFromDataURL(scalebarImage);
    const scaleFactor = rect.w / pxToMm(scabeBarWidth);
    doc.addImage(scalebarImage, 'PNG', rect.x, rect.y+pxToMm(80), pxToMm(scabeBarWidth)*scaleFactor, pxToMm(scaleBarHeight)*scaleFactor);
  }

  private waitForMetadataElementToLayout(element: HTMLElement): Promise<void> {
    return new Promise(resolve => {
      const check = () => {
        const isReady = element.offsetHeight > 0 && element.innerText.trim().length > 0;
        if (isReady) {
          requestAnimationFrame(() => resolve()); // ensure 1 more paint cycle
        } else {
          requestAnimationFrame(check);
        }
      };
      check();
    });
  }

  //helper function to add an image from the assets/images folder
  async addImageToPdf(
  pdf: jsPDF,
  image: string,
  rect: Rect,
  options?: {
    preserveOriginalSize?: boolean;
    align?: {
      horizontal?: 'left' | 'center' | 'right';
      vertical?: 'top' | 'center' | 'bottom';
    };
  }
  ): Promise<void> {
  const isUrl = image.startsWith('http://') || image.startsWith('https://');
  const imagePath = isUrl ? image : `assets/images/${image}`;

  const loadImage = async (src: string): Promise<{ img : HTMLImageElement; format : string}> => {
    const response = await fetch(src);
    if(!response.ok) throw new Error(`Failed to load image: ${response.statusText}`);
    const contentType = response.headers.get('Content-Type') ?? '';
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const img : HTMLImageElement = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    let format = 'PNG';
    if (contentType.includes('jpeg') || contentType.includes('jpg')) {
      format = 'JPEG';
    } else if (contentType.includes('png')) {
      format = 'PNG';
    } else if (contentType.includes('svg')) {
      format = 'SVG';
    }
    return {img, format};
  };

  const calculatePosition = (
    imgWidth: number,
    imgHeight: number,
    rect: Rect,
    align: { horizontal: string; vertical: string }
  ): { x: number; y: number } => {
    let x = rect.x;
    let y = rect.y;

    // Horizontal
    switch (align.horizontal) {
      case 'center':
        x += (rect.w - imgWidth) / 2;
        break;
      case 'right':
        x += rect.w - imgWidth;
        break;
      // 'left' or default is just rect.x
    }

    // Vertical
    switch (align.vertical) {
      case 'center':
        y += (rect.h - imgHeight) / 2;
        break;
      case 'bottom':
        y += rect.h - imgHeight;
        break;
      // 'top' or default is just rect.y
    }

    return { x, y };
  };

  const {img, format} = await loadImage(imagePath);

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.drawImage(img, 0, 0);

  const base64 = canvas.toDataURL(`image/${format.toLowerCase()}`);

  const preserveOriginal = options?.preserveOriginalSize === true;
  const align = {
    horizontal: options?.align?.horizontal ?? 'center',
    vertical: options?.align?.vertical ?? 'center',
  };

  let drawWidth: number;
  let drawHeight: number;

  if (preserveOriginal) {
    // jsPDF default unit is mm unless specified
    // So we convert image pixels to jsPDF units based on scaleFactor
    const scale = pdf.internal.scaleFactor;
    drawWidth = img.width / scale;
    drawHeight = img.height / scale;
  } else {
    // Fit into rect while preserving aspect ratio
    const imgRatio = img.width / img.height;
    const rectRatio = rect.w / rect.h;

    if (imgRatio > rectRatio) {
      drawWidth = rect.w;
      drawHeight = rect.w / imgRatio;
    } else {
      drawHeight = rect.h;
      drawWidth = rect.h * imgRatio;
    }
  }

  const { x, y } = calculatePosition(drawWidth, drawHeight, rect, align);
  pdf.addImage(base64, format, x, y, drawWidth, drawHeight);
  }
private async waitForMapRender(map: Map): Promise<"rendercomplete" | "timeout"> {
  return new Promise(resolve => {
    let resolved = false;


    const fallback = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve("timeout");
      }
    }, 60000); // fallback timeout in ms

    map.once('rendercomplete', () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(fallback);
        resolve("rendercomplete");
      }
    });
  });
}

  private timeout(ms):Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async waitForElementToLayout(element: HTMLElement): Promise<void> {
    return new Promise(resolve => {
      const check = () => {
        if (element.offsetHeight > 0 && element.innerText.trim().length > 0) {
          requestAnimationFrame(() => resolve());
        } else {
          requestAnimationFrame(check);
        }
      };
      check();
    });
  }
  private getFileName(row:Feature): string {
    const fileNamePrefix = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.dossier');
    const noDossier = row?.properties['No dossier'] ?? '';
    const safeDossier = noDossier?.trim() ? noDossier : 'GDF';
    const pdfFileName = `${fileNamePrefix}-${safeDossier}.pdf`;
    return pdfFileName;
  }

  private addLayerToMap(map: Map, layer: Layer, visible: boolean) {
    const olLayer = layer.ol;
    let clonedLayer: BaseLayer | null = null;

      // Clone a VectorLayer
  if (olLayer instanceof OlVectorLayer) {
    console.debug('add Vector Layer to Map');
    const originalSource = olLayer.getSource();
    const originalFeatures = originalSource?.getFeatures() || [];

    const geojson = new GeoJSON();
    const clonedFeatures = geojson.readFeatures(
      geojson.writeFeatures(originalFeatures),
      { featureProjection: 'EPSG:3857' }
    );

    const clonedSource = new VectorSource({
      features: clonedFeatures,
    });

    const originalStyle = olLayer.getStyle();
    const clonedStyle = typeof originalStyle === 'function'
      ? originalStyle // You might need to clone or wrap this function if it's dynamic
      : originalStyle ? this.cloneStyleFunction(originalStyle as Style | Style[]) : undefined;

    clonedLayer = new OlVectorLayer({
      source: clonedSource,
      style: clonedStyle,
      opacity: olLayer.getOpacity(),
      visible: visible,
      zIndex: olLayer.getZIndex(),
    });
  }

  // Clone a TileLayer (safe to reuse source)
  else if (olLayer instanceof TileLayer) {
    console.debug('add TileLayer Layer to Map');

    clonedLayer = new TileLayer({
      source: olLayer.getSource(),
      opacity: olLayer.getOpacity(),
      visible: visible,
      zIndex: olLayer.getZIndex(),
    });
  }
    // Clone ImageLayer (source can usually be reused safely)
  else if (olLayer instanceof ImageLayer) {
    console.debug('add ImageLayer Layer to Map');

    const originalSource = olLayer.getSource();
    const clonedSource = new ImageWMS({
      url: originalSource.getUrl(),
      params: { ...originalSource.getParams() },
      ratio: originalSource.get('ratio'),
      serverType: originalSource.get('serverType'),
      crossOrigin: 'anonymous',
      projection: originalSource.getProjection?.(),
    });

    clonedLayer = new ImageLayer({
      source: clonedSource,
      opacity: olLayer.getOpacity(),
      visible: visible,
      zIndex: olLayer.getZIndex(),
    });
  }

  // You can add more cases here for ImageLayer, HeatmapLayer, etc.

  if (clonedLayer) {
    map.addLayer(clonedLayer);
  } else {
    console.warn('Unsupported layer type for cloning:', olLayer);
  }
  }
  private cloneStyleFunction(style: Style|  Style[] | ((...args: any[]) => Style) | undefined): typeof style {
    if (!style) return undefined;

    if (typeof style === 'function') {
      console.warn('Style is a function and will be reused as-is. If it depends on map state, consider wrapping or rewriting it.');
      return style;
    }
    if (Array.isArray(style)) {
      return style.map(s => s.clone()) as Style[];
    }
    return style.clone();
  }

  //utility function to strip unwanted information from the title
  private formatFins(fins: string):string {
    if (!fins.trim()) {
      return '';
    }

    // Remove the numeric code and separator (e.g., "nn - ")
    const description = fins.replace(/^\d+\s*-\s*/, '');

    // Capitalize the first character
    return description.charAt(0).toUpperCase() + description.slice(1);
  }
  // get the image size
  private async getImageSizeFromDataURL(dataURL: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (err) => {
        reject(new Error("Failed to load image"));
      };
      img.src = dataURL;
    });
  }

  private addMapFrame(pdf:jsPDF, rect: Rect): void {
    //addmapframe
    const curDrawColor = pdf.getDrawColor();
    const curLineWidth = pdf.getLineWidth();
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth((0.5 / 72)*25.4);
    pdf.rect(rect.x, rect.y, rect.w, rect.h,"D");
    pdf.setDrawColor(curDrawColor);
    pdf.setLineWidth(curLineWidth);
  }

  private toggleOverLayLayer(olMap:Map, layer:VectorLayer, visible: boolean): void {
    const overlayLayer = olMap.getAllLayers().filter(l=>l.get('title')===layer.ol.get('title'));
    overlayLayer?.forEach(ol => {
      ol.setVisible(visible);        
    });
  }

  private getMTMCoordinate(label:string, mapCenterPoint?:Coordinate, projection?:string):string {   
    if (mapCenterPoint){
      const longLat = transform(mapCenterPoint, projection, 'EPSG:4326');
      const fuseau=zoneMtm(longLat[0]);
      if (fuseau) {
        const epsgMtm =
          fuseau < 10 ? `EPSG:3218${fuseau}` : `EPSG:321${80 + fuseau}`;

        const rawCoordMtm = transform(mapCenterPoint, projection, epsgMtm);
        const x = rawCoordMtm[0].toFixed(0);
        const y = rawCoordMtm[1].toFixed(0);
        const text = `${label} MTM ${fuseau} ${x} ${y}`;     
        return text;
      }
    }
    return null;
  }
  
  disposeCanvas(canvas?: HTMLCanvasElement | null) {
    try {
      if (!canvas) return;
      // Clear the bitmap backing store
      canvas.width = 0;
      canvas.height = 0;
    } catch {}
  }

}
